import supabase from '../supabase/supabase';

export const glService = {
  /**
   * Fetch all ledger transactions with optional filters.
   * A ledger read failure must not prevent the General Ledger page from
   * loading its Chart of Accounts filter.
   */
  async getLedgerEntries({ accountId, startDate, endDate, coaId }) {
    try {
      let query = supabase
        .schema('accounting')
        .from('transactions_20240522')
        .select(`
          *,
          coa:chart_of_accounts_20240522!inner(name, code, type)
        `)
        .eq('account_id', accountId)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false });

      if (startDate) query = query.gte('date', startDate);
      if (endDate) query = query.lte('date', endDate);
      if (coaId) query = query.eq('coa_id', coaId);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching ledger entries:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching ledger entries:', error);
      return [];
    }
  },

  /**
   * Create a balanced manual journal entry
   */
  async createJournalEntry(accountId, { date, description, lines }) {
    try {
      const totalDebits = lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
      const totalCredits = lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);

      if (Math.abs(totalDebits - totalCredits) > 0.01) {
        throw new Error('Journal entry is not balanced. Debits must equal Credits.');
      }

      if (lines.length < 2) {
        throw new Error('Journal entry must have at least two lines.');
      }

      const batchId = crypto.randomUUID();
      const transactionLines = lines.map(line => ({
        account_id: accountId,
        coa_id: line.coa_id,
        date: date,
        description: line.description || description,
        debit: Number(line.debit || 0),
        credit: Number(line.credit || 0),
        reference_type: 'Journal',
        batch_id: batchId,
        is_manual: true,
        status: 'Cleared'
      }));

      const { data, error } = await supabase.rpc('post_manual_journal_entry', {
        p_account_id: accountId,
        p_entry_date: date,
        p_description: description || '',
        p_lines: transactionLines
      });

      if (error) throw error;

      const { data: journalLines, error: journalLinesError } = await supabase
        .schema('accounting')
        .from('transactions_20240522')
        .select('*')
        .eq('journal_entry_id', data)
        .order('created_at', { ascending: true });

      if (journalLinesError) throw journalLinesError;
      return journalLines;
    } catch (error) {
      console.error('Error creating journal entry:', error);
      throw error;
    }
  },

  /**
   * Reverse a journal entry instead of deleting accounting history.
   */
  async deleteJournalEntry(batchId) {
    const { data, error } = await supabase.rpc('reverse_manual_journal_entry', {
      p_journal_entry_id: batchId,
      p_reversal_date: new Date().toISOString().split('T')[0],
      p_reason: 'Reversed from General Ledger'
    });

    if (error) throw error;
    return data;
  }
};